<template>
  <div class="login-page">
    <div
      class="login-card"
      data-cy="login-header-card"
    >
      <div
        v-if="apiClient == null || orgs == null"
        class="loader-row"
      >
        <ngc-loading-spinner
          size="medium"
          aria-label="Loading"
        />
      </div>
      <template v-else>
        <div class="logo-row mb-6">
          <img
            :src="logoNoText"
            class="consent-logo"
            alt="nCino eSign"
            data-cy="logo-no-text"
          >
          <ngc-icon
            class="mx-6"
            name="tab-arrows"
            size="xlarge"
          />
          <img
            v-if="apiClientLogo"
            :src="apiClientLogo"
            class="consent-logo"
            :alt="apiClient.name"
            data-cy="logo-no-text"
          >
          <span
            v-else class="gator-body-1 gator-body-bold no-logo"
          >
            {{ apiClient.name }}
          </span>
        </div>

        <div class="title-row mb-6">
          <div
            class="gator-heading-h2"
            data-cy="title"
            style="text-align: center"
          >
            Connect nCino eSign to {{ apiClient.name }}
          </div>

          <ngc-input-dropdown
            v-if="orgs.length > 1"
            class="org-selector"
            label="Select which organization to connect:"
            placeholder="Select an organization"
            data-cy="mers-org-selector"
            :required="true"
            :hide-required="true"
            :value="selectedMersOrgId"
            :options.prop="orgOptions"
            @change="handleOrgChange"
          />

          <div v-else>
            {{ orgs[0].optionLabel }}
          </div>
        </div>

        <div class="mb-6">
          <div class="pt-6 pb-7 px-4 permissions">
            <span class="gator-body-2 gator-body-bold mb-4">Allow {{ apiClient.name }} to perform the following:</span>
            <ul>
              <li
                v-for="permission in consentPermissions"
                :key="permission"
                class="gator-body-2"
              >
                {{ permission }}
              </li>
            </ul>
          </div>
        </div>

        <div class="button-row mb-6">
          <ngc-button
            class="connect mb-4"
            variant="primary"
            aria-label="connect"
            data-cy="connect"
            size="large"
            :loading="loading"
            :disabled="selectedMersOrgId == null"
            @click="authorize"
          >
            Connect
          </ngc-button>
          <ngc-button
            class="cancel"
            variant="secondary"
            aria-label="cancel"
            data-cy="cancel"
            size="large"
            :disabled="loading"
            @click="cancelConsent"
          >
            Cancel
          </ngc-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>

import logoNoText from '~/assets/images/logos/ncino_logo_no_text_color.png'
import { apiClient, authorizeApiClient } from './graphql/api_client_queries.gql'
import { orgs } from './graphql/signing_room_queries.gql'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'Consent',
  data () {
    return {
      loading: false,
      logoNoText: logoNoText,
      clientId: null,
      redirectUri: null,
      state: null,
      orgs: null,
      selectedMersOrgId: null,
      apiClient: null,
      consentPermissions: null
    }
  },
  computed: {
    ...mapGetters(['user']),
    apiClientLogo () {
      const imgString = this.apiClient.logoAsBase64
      if (imgString) {
        return `data:image/png;base64,${imgString}`
      }
      return undefined
    },
    orgOptions () {
      if (!this.orgs) return []
      return this.orgs.map(org => ({
        label: org.optionLabel,
        text: org.optionLabel,
        value: org.guid
      }))
    }
  },
  mounted () {
    this.clientId = this.$route.params.guid
    this.redirectUri = this.$route.query.redirect_uri
    this.state = this.$route.query.state

    this.getApiClient()
    this.getOrgs()
  },
  methods: {
    ...mapMutations(['setHeaderText', 'setSubtitleText', 'setBreadcrumbs', 'setHeaderTabs', 'setSuccessToast', 'setErrorToastType', 'setActiveHeaderTab']),
    getApiClient () {
      return this.$apollo.query({
        query: apiClient,
        variables: {
          clientId: this.$route.params.guid,
          runInquiry: true
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        this.apiClient = data.apiClient
        this.consentPermissions = JSON.parse(this.apiClient.consentPermissions)
        return this.apiClient
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },

    getOrgs () {
      return this.$apollo.query({
        query: orgs,
        variables: {
          companyGuid: null,
          order: 'remote_id ASC',
          active: true
        }
      }).then(({ data }) => {
        this.orgs = data.orgs.nodes
        this.orgs.forEach(org => { org.optionLabel = org.remoteId ? `${org.remoteId} ${org.name}` : org.name })

        if (this.orgs.length === 1) {
          this.selectedMersOrgId = this.orgs[0].guid
        }

        return this.orgs
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },

    cancelConsent () {
      sessionStorage.clear()
      window.location.href = `${this.redirectUri}?state=${this.state}`
    },

    handleOrgChange (event) {
      this.selectedMersOrgId = event.detail?.value ?? null
    },

    authorize () {
      this.loading = true
      this.$apollo.mutate({
        mutation: authorizeApiClient,
        variables: {
          orgGuid: this.selectedMersOrgId,
          apiClientGuid: this.apiClient.guid
        }
      }).then(({ data }) => {
        this.loading = false
        if (data.authorizeApiClient.error) {
          this.setErrorToastType({ description: data.authorizeApiClient.error })
        } else {
          const guid = data.authorizeApiClient.guid
          window.location.href = `${this.redirectUri}?code=${guid}&state=${this.state}`
        }
      }).catch(err => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.logo-row,
.title-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

.consent-logo {
  max-width: 71px;
  height: auto;
}

.loader-row {
  display: flex;
  justify-content: center;
  padding: 32px 0 10px;
}

.button-row {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  .connect,
  .cancel {
    width: 100%;
    --ngc-button-width: 100%;
  }

  .connect::part(button),
  .cancel::part(button) {
    width: 100%;
  }
}

.org-selector {
  margin-top: 12px;
  width: 100%;
}

.login-page {
  min-height: 100vh;
  background: #F7F7F7;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 100px 16px 24px;
}

.login-card {
  width: 100%;
  max-width: 535px;
  background: #FFFFFF;
  border-radius: 16px;
  box-shadow:
    0 1px 1.5px rgba(31, 31, 31, 0.04),
    0 2px 5px rgba(31, 31, 31, 0.10),
    0 3px 9px 6px rgba(31, 31, 31, 0.05);
  padding: 40px;
  box-sizing: border-box;
}

.permissions {
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 8px;
  background-color: #F7F7F7;
}

.no-logo {
  display: inline-block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
